import { createRoot } from 'react-dom/client';
import { memo, useState, useEffect } from 'react';
import { TeacherPageContextProvider } from './TeacherPageContextProvider';
import { TeacherProfileBanner } from './TeacherProfileBanner';
import { PHOTO_TYPE, PhotoTypes, TeacherProfileVO } from './types';
import TeacherProfileQuickDonateCardSection from './TeacherProfileQuickDonateCardSection';
import TeacherProfileAboutSection from './TeacherProfileAboutSection';
import TeacherProfileTrustBar from './TeacherProfileTrustBar';
import TeacherProfileShareBar from './TeacherProfileShareBar';
import TeacherPhotoUploadModal from './TeacherPhotoUploadModal';
import { PhotoUploadConfig } from '../common/photoupload/types';
import { ServerPhotoTypes } from './usePhotoProcessingStatus';

import '../../../scss/features/teacher/teacherpage.scss';

const resetCookiesAndReload = () => {
    $.ajax({
        url: secureBaseURL + 'common/reset_user_cookie.html',
        xhrFields: {
            withCredentials: true,
        },
        dataType: 'json',
    }).always(() => {
        // Load the page again with a cachebust (to beat Fastly-caching of page contents). Use `always` because a
        // failure-response can still alter cookies.
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.append('cacheBust', new Date().getTime().toString(10));
        window.location.href = currentUrl.toString();
    });
};

interface TeacherProfileMainProps {
    teacherProfileVO: TeacherProfileVO,
}

const TeacherProfileMain = ({ teacherProfileVO }: TeacherProfileMainProps) => {
    const [uploadModalPhotoType, setUploadModalPhotoType] = useState<PHOTO_TYPE>(null);
    const [config, setConfig] = useState<PhotoUploadConfig | null>(null);

    useEffect(() => {
        if (!uploadModalPhotoType) {
            setConfig(null);
            return;
        }

        const serverPhotoType = uploadModalPhotoType === PhotoTypes.PROFILE
            ? ServerPhotoTypes.TEACHER_PROFILE
            : ServerPhotoTypes.USER;

        $.ajax(`${secureBaseURL}teacher/profile_photo_upload.html?photoType=${serverPhotoType}`, {
            method: 'GET',
            headers: {
                Accept: 'application/json',
            },
            xhrFields: {
                withCredentials: true,
            },
        }).then((res) => {
            if (!res) {
                throw new Error('Invalid config returned');
            }
            setConfig({
                credentialsUrl: res.credentialsUrl,
                minPhotos: 1,
                maxPhotos: 1,
                minImageWidth: res.minImageWidth,
                minImageHeight: res.minImageHeight,
                displayUrlParams: '',
                // Default on-error behavior is Known Bad for classroom photos
                suppressDefaultErrorBehavior: serverPhotoType === ServerPhotoTypes.USER,
                callbackData: { isMobile: String(window.isMobileDevice) },
            });
        }).catch((err) => {
            console.log(err);
        });
    }, [uploadModalPhotoType]);

    return (
        <div>
            <TeacherProfileBanner setUploadModalPhotoType={setUploadModalPhotoType}
                teacherProfileVO={teacherProfileVO}></TeacherProfileBanner>
            <TeacherProfileTrustBar/>
            <TeacherProfileAboutSection vanityUrl={teacherProfileVO.vanityUrl}
                pageUrl={teacherProfileVO.teacherProfileUrl}
                teacherDetails={teacherProfileVO.teacherDetails}
                schoolDetails={teacherProfileVO.schoolDetails}
                setUploadModalPhotoType={setUploadModalPhotoType}
            />
            {uploadModalPhotoType && config != null && (
                <TeacherPhotoUploadModal
                    photoType={uploadModalPhotoType}
                    teacherId={teacherProfileVO.teacherDetails.id}
                    currentPhotoUrl={uploadModalPhotoType === PhotoTypes.PROFILE ? teacherProfileVO.teacherDetails.profilePhotoUrl : teacherProfileVO.teacherDetails.classroomPhotoUrl}
                    onPhotoUpdated={() => resetCookiesAndReload()}
                    onClose={() => setUploadModalPhotoType(null)}
                    config={config}
                />
            )}
            <TeacherProfileQuickDonateCardSection teacherDetails={teacherProfileVO.teacherDetails}
                essentialsListDetails={teacherProfileVO.essentialsListDetails}
            ></TeacherProfileQuickDonateCardSection>
            <TeacherProfileShareBar
                urlToShare={teacherProfileVO.teacherProfileUrl}
                teacherId={teacherProfileVO.teacherDetails.id}
            >
            </TeacherProfileShareBar>
        </div>
    );
};

$(() => {
    const contentElement: HTMLElement = document.getElementById('content')!;
    let teacherProfileVO;
    if (contentElement.dataset.teacherProfileVO) {
        teacherProfileVO = JSON.parse(contentElement.dataset.teacherProfileVO!);
    }

    // Some of our code expects a global `window.teacherId` to be set
    window.teacherId = teacherProfileVO.teacherDetails.id;
    const root = createRoot(contentElement);

    root.render(<div>
        <TeacherPageContextProvider>
            <TeacherProfileMain
                teacherProfileVO={teacherProfileVO}>
            </TeacherProfileMain>
        </TeacherPageContextProvider>
    </div>);
});

export default memo(TeacherProfileMain);
